import { useEffect, useRef, useState, useCallback } from "react";
import { whatsappAPI } from "../../lib/api";
import {
    MessageCircle, Search, Send, RefreshCw, CheckCheck,
    Check, Plus, Trash2, X, AlertCircle, Wifi, WifiOff,
} from "lucide-react";
import { toast } from "sonner";

const TEMPLATE_CATEGORIES = [
    { value: "devocional",  label: "Devocional" },
    { value: "oferta",      label: "Oferta / Promoção" },
    { value: "lancamento",  label: "Lançamento" },
    { value: "evento",      label: "Evento" },
    { value: "boas_vindas", label: "Boas-vindas" },
];

function Avatar({ name = "", size = 36 }) {
    const initials = name.split(" ").slice(0,2).map(w => w[0]).join("").toUpperCase() || "?";
    const colors = ["bg-amber-600","bg-rose-600","bg-indigo-600","bg-sky-600","bg-violet-600","bg-emerald-600"];
    const bg = colors[(name.charCodeAt(0) || 0) % colors.length];
    return (
        <div className={`${bg} rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white`}
            style={{ width: size, height: size, fontSize: size * 0.35 }}>
            {initials}
        </div>
    );
}

function fmtTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    if (now - d < 86400000) return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

function TemplateModal({ open, onClose, onSaved, editing }) {
    const [form, setForm] = useState({ name: "", category: "boas_vindas", meta_template_name: "", active: false });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (editing) setForm({ name: editing.name, category: editing.category, meta_template_name: editing.meta_template_name || "", active: editing.active });
        else setForm({ name: "", category: "boas_vindas", meta_template_name: "", active: false });
    }, [editing, open]);

    const save = async () => {
        if (!form.name || !form.meta_template_name) { toast.error("Preencha nome e nome do template na Meta"); return; }
        setSaving(true);
        try {
            if (editing) await whatsappAPI.updateTemplate(editing.id, form);
            else         await whatsappAPI.createTemplate(form);
            toast.success(editing ? "Template atualizado" : "Template criado");
            onSaved();
            onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
        finally { setSaving(false); }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white border border-gray-200 rounded-2xl w-full max-w-md shadow-2xl">
                <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="font-bold text-gray-900">{editing ? "Editar" : "Novo"} Template WhatsApp</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-3">
                    <div>
                        <label className="text-xs text-gray-500 block mb-1">Nome interno</label>
                        <input className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-[#A0462D] transition-colors"
                            value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="ex: Boas-vindas Devocional" />
                    </div>
                    <div>
                        <label className="text-xs text-gray-500 block mb-1">Categoria</label>
                        <select className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-[#A0462D] transition-colors"
                            value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))}>
                            {TEMPLATE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-gray-500 block mb-1">Nome na Meta (aprovado)</label>
                        <input className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-[#A0462D] transition-colors"
                            value={form.meta_template_name} onChange={e => setForm(f => ({...f, meta_template_name: e.target.value}))}
                            placeholder="ex: boas_vindas_devocional_v1" />
                        <p className="text-xs text-gray-400 mt-1">Nome exato como aparece no Meta Business Suite após aprovação.</p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({...f, active: e.target.checked}))} />
                        Template ativo (disponível nos fluxos)
                    </label>
                </div>
                <div className="p-5 border-t border-gray-100 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">Cancelar</button>
                    <button onClick={save} disabled={saving}
                        className="px-4 py-2 text-white text-sm rounded-lg disabled:opacity-50 transition-colors"
                        style={{ background: "#16a34a" }}>
                        {saving ? "Salvando..." : "Salvar"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function WhatsAppPage() {
    const [tab, setTab] = useState("chat");
    const [status, setStatus] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [messages, setMessages] = useState([]);
    const [activeJid, setActiveJid] = useState(null);
    const [search, setSearch] = useState("");
    const [templates, setTemplates] = useState([]);
    const [templateModal, setTemplateModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sendForm, setSendForm] = useState({ phone: "", template_id: "", variables: {} });
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef(null);

    const loadStatus = useCallback(async () => {
        try { const r = await whatsappAPI.status(); setStatus(r.data); } catch {}
    }, []);
    const loadConversations = useCallback(async () => {
        try { const r = await whatsappAPI.conversations(); setConversations(r.data || []); } catch {}
    }, []);
    const loadTemplates = useCallback(async () => {
        try { const r = await whatsappAPI.listTemplates(); setTemplates(r.data || []); } catch {}
    }, []);

    useEffect(() => {
        Promise.all([loadStatus(), loadConversations(), loadTemplates()]).finally(() => setLoading(false));
        const poll = setInterval(() => { loadConversations(); }, 15000);
        return () => clearInterval(poll);
    }, []);

    useEffect(() => {
        if (!activeJid) return;
        whatsappAPI.messages(activeJid).then(r => {
            setMessages(r.data || []);
            setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
        }).catch(() => {});
    }, [activeJid]);

    const sendMessage = async () => {
        if (!sendForm.phone || !sendForm.template_id) { toast.error("Preencha telefone e template"); return; }
        setSending(true);
        try {
            await whatsappAPI.send(sendForm);
            toast.success("Mensagem enviada via Meta Cloud API");
            setSendForm({ phone: "", template_id: "", variables: {} });
        } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar"); }
        finally { setSending(false); }
    };

    const deleteTemplate = async (id) => {
        if (!window.confirm("Excluir template?")) return;
        try { await whatsappAPI.deleteTemplate(id); toast.success("Excluído"); loadTemplates(); }
        catch { toast.error("Erro ao excluir"); }
    };

    const filteredConvs = conversations.filter(c =>
        !search || (c._id || "").includes(search) || (c.last_message || "").toLowerCase().includes(search.toLowerCase())
    );

    const BRAND = "#A0462D";
    const TABS = [["chat","Conversas"],["templates","Templates"],["send","Enviar"]];

    return (
        <div className="h-[calc(100vh-0px)] flex flex-col bg-white">
            <TemplateModal open={templateModal} onClose={() => { setTemplateModal(false); setEditingTemplate(null); }}
                onSaved={loadTemplates} editing={editingTemplate} />

            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <MessageCircle className="w-5 h-5 text-green-600" />
                        <h1 className="text-lg font-bold text-gray-900">WhatsApp</h1>
                        {status && (
                            <div className={`flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border ${
                                status.configured
                                    ? "bg-green-50 text-green-700 border-green-200"
                                    : "bg-red-50 text-red-600 border-red-200"
                            }`}>
                                {status.configured ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                                {status.configured ? "Meta Cloud API conectada" : "Não configurado"}
                            </div>
                        )}
                    </div>
                    <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                        {TABS.map(([t, l]) => (
                            <button key={t} onClick={() => setTab(t)}
                                className={`px-3 py-1.5 text-xs rounded-md transition-all ${
                                    tab === t ? "bg-white text-gray-900 font-medium shadow-sm" : "text-gray-500 hover:text-gray-700"
                                }`}>
                                {l}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {status && !status.configured && (
                <div className="m-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-medium text-amber-800">Meta Cloud API não configurada</p>
                        <p className="text-xs text-amber-600 mt-1">
                            Configure as variáveis{" "}
                            <code className="bg-amber-100 px-1 rounded">META_WABA_TOKEN</code>,{" "}
                            <code className="bg-amber-100 px-1 rounded">META_PHONE_ID</code> e{" "}
                            <code className="bg-amber-100 px-1 rounded">META_VERIFY_TOKEN</code> no .env do backend.
                        </p>
                    </div>
                </div>
            )}

            {tab === "chat" && (
                <div className="flex flex-1 overflow-hidden">
                    <div className="w-80 border-r border-gray-200 flex flex-col bg-gray-50">
                        <div className="p-3 border-b border-gray-200">
                            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
                                <Search className="w-3.5 h-3.5 text-gray-400" />
                                <input className="bg-transparent text-xs text-gray-700 placeholder:text-gray-400 focus:outline-none flex-1"
                                    placeholder="Buscar conversa..." value={search} onChange={e => setSearch(e.target.value)} />
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                            {filteredConvs.length === 0 && (
                                <div className="text-center py-12 text-gray-400 text-xs">Nenhuma conversa</div>
                            )}
                            {filteredConvs.map(conv => (
                                <button key={conv._id} onClick={() => setActiveJid(conv._id)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-white ${activeJid === conv._id ? "bg-white" : ""}`}
                                    style={activeJid === conv._id ? { borderRight: `2px solid ${BRAND}` } : {}}>
                                    <Avatar name={conv._id || "?"} size={36} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                            <p className="text-xs font-medium text-gray-800 truncate">{conv._id}</p>
                                            <span className="text-xs text-gray-400 ml-1 flex-shrink-0">{fmtTime(conv.last_message_at)}</span>
                                        </div>
                                        <p className="text-xs text-gray-500 truncate mt-0.5">{conv.last_message || "—"}</p>
                                    </div>
                                    {conv.unread > 0 && (
                                        <span className="w-5 h-5 bg-green-600 rounded-full text-white text-xs flex items-center justify-center flex-shrink-0">
                                            {conv.unread}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex-1 flex flex-col bg-white">
                        {!activeJid ? (
                            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-400">
                                <MessageCircle className="w-10 h-10 text-gray-200" />
                                <p className="text-sm">Selecione uma conversa</p>
                            </div>
                        ) : (
                            <>
                                <div className="border-b border-gray-200 px-4 py-3 flex items-center gap-3 bg-gray-50">
                                    <Avatar name={activeJid} size={32} />
                                    <span className="text-sm font-medium text-gray-800">{activeJid}</span>
                                    <button onClick={() => whatsappAPI.messages(activeJid).then(r => setMessages(r.data || []))}
                                        className="ml-auto p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors">
                                        <RefreshCw className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                                <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: "#f0f2f5" }}>
                                    {messages.map(msg => (
                                        <div key={msg.id} className={`flex ${msg.direction === "out" ? "justify-end" : "justify-start"}`}>
                                            <div className={`max-w-xs rounded-2xl px-3 py-2 text-sm shadow-sm ${
                                                msg.direction === "out"
                                                    ? "bg-green-600 text-white rounded-br-sm"
                                                    : "bg-white text-gray-800 rounded-bl-sm"
                                            }`}>
                                                <p>{msg.content}</p>
                                                <div className="flex items-center justify-end gap-1 mt-1">
                                                    <span className="text-xs opacity-60">{fmtTime(msg.created_at)}</span>
                                                    {msg.direction === "out" && (
                                                        msg.status === "read" ? <CheckCheck className="w-3 h-3 text-blue-200" /> : <Check className="w-3 h-3 opacity-60" />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <div ref={messagesEndRef} />
                                </div>
                                <div className="border-t border-gray-200 p-3 bg-white">
                                    <p className="text-xs text-gray-400 text-center">
                                        Para enviar fora de janela de 24h, use a aba <strong>Enviar</strong> com template aprovado.
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {tab === "templates" && (
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-base font-semibold text-gray-900">Templates Meta Cloud API</h2>
                            <p className="text-xs text-gray-500 mt-0.5">Templates precisam ser aprovados pela Meta antes de uso.</p>
                        </div>
                        <button onClick={() => setTemplateModal(true)}
                            className="flex items-center gap-2 text-white text-sm px-4 py-2 rounded-lg transition-colors hover:opacity-90"
                            style={{ background: "#16a34a" }}>
                            <Plus className="w-4 h-4" /> Novo Template
                        </button>
                    </div>

                    {templates.length === 0 ? (
                        <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl p-10 text-center">
                            <MessageCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 text-sm">Nenhum template cadastrado.</p>
                            <p className="text-gray-400 text-xs mt-1">Cadastre os templates aprovados pela Meta para usar nos fluxos.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {templates.map(tpl => (
                                <div key={tpl.id} className="bg-white border border-gray-200 rounded-xl p-4 hover:border-gray-300 hover:shadow-sm transition-all">
                                    <div className="flex items-start justify-between mb-2">
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{tpl.name}</p>
                                            <p className="text-xs text-gray-500 mt-0.5">{TEMPLATE_CATEGORIES.find(c => c.value === tpl.category)?.label || tpl.category}</p>
                                        </div>
                                        <span className={`text-xs px-2 py-0.5 rounded-full border ${tpl.active ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-50 text-gray-500 border-gray-200"}`}>
                                            {tpl.active ? "Ativo" : "Inativo"}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-600 font-mono bg-gray-50 rounded-lg px-2 py-1 mt-2 truncate border border-gray-100">
                                        {tpl.meta_template_name || "sem nome Meta"}
                                    </p>
                                    <div className="flex items-center gap-2 mt-3">
                                        <button onClick={() => { setEditingTemplate(tpl); setTemplateModal(true); }}
                                            className="flex-1 text-xs py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors">
                                            Editar
                                        </button>
                                        <button onClick={() => deleteTemplate(tpl.id)}
                                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {tab === "send" && (
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="max-w-md space-y-5">
                        <div>
                            <h2 className="text-base font-semibold text-gray-900">Envio manual</h2>
                            <p className="text-xs text-gray-500 mt-0.5">Envie um template aprovado para um número específico.</p>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs text-gray-500 block mb-1">Número (com DDD e DDI)</label>
                                <input className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-green-500 transition-colors"
                                    value={sendForm.phone} onChange={e => setSendForm(f => ({...f, phone: e.target.value}))}
                                    placeholder="5547999990000" />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 block mb-1">Template</label>
                                <select className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-green-500 transition-colors"
                                    value={sendForm.template_id} onChange={e => setSendForm(f => ({...f, template_id: e.target.value}))}>
                                    <option value="">Selecionar template</option>
                                    {templates.filter(t => t.active).map(t => (
                                        <option key={t.id} value={t.id}>{t.name} ({t.category})</option>
                                    ))}
                                </select>
                                {templates.filter(t => t.active).length === 0 && (
                                    <p className="text-xs text-gray-400 mt-1">Nenhum template ativo. Ative um na aba Templates.</p>
                                )}
                            </div>
                            <button onClick={sendMessage} disabled={sending || !status?.configured}
                                className="w-full flex items-center justify-center gap-2 text-white py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
                                style={{ background: "#16a34a" }}>
                                <Send className="w-4 h-4" />
                                {sending ? "Enviando..." : "Enviar via Meta Cloud API"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
